import { IconName } from "@pathwright/pathicons"
import { ReactNode } from "react"
import { CoverPageOverviewImmersive } from "../CoverPageDescription"
import CoverPageMore from "../CoverPageMore"
import { CoverPageOfferImmersive } from "../CoverPageOffer"
import CoverPageContributorImmersive from "../contributors/CoverPageContributorImmersive"
import { CoverPagePreviewImmersive } from "../path-preview/CoverPagePreviewImmersive"
import { CoverPageOfferSandboxImmersive } from "../tests/CoverPageOfferSandbox"
import { CoverPageTrailerImmersive } from "../trailer/CoverPageTrailer"
import { CoverPageContextType, ImmersiveRoute } from "../types"

// This (admittedly somewhat complex) configuration allows us to
// define Immersive route configuration in a single place.
// Used by React Router and for UI navigation.

export type ImmersiveRouteConfig = {
  key: ImmersiveRoute["name"]
  // React Router path pattern(s) used for route config
  pathRe: string | string[]
  // Build a navigate-able path from base and an ImmersiveRoute payload
  getPath: (args: { baseCover: string; route: ImmersiveRoute }) => string | null
  // Returns true when this route is currently active;
  // designed to be used by route handlers
  isActive: (args: { matchPath: string; params?: any }) => boolean
  // Build the ImmersiveRoute payload from route params
  getImmersiveRoute: (params: any) => ImmersiveRoute | null
  // Get immersive routes for routes that are actually active (based on context data)
  getNavigableImmersiveRoutes: (
    coverPageContext: CoverPageContextType
  ) => ImmersiveRoute[]
  // Render function for the immersive content
  render: (args?: any) => JSX.Element
  // Should this route be rendered at all?
  shouldRender?: (coverPageContext: CoverPageContextType) => boolean
  // If displayAsNavLabel is true, this label will be displayed in the navigation
  navLabel?: string | ReactNode
  // Icon to display in the navigation
  navIcon?: IconName
  // Whether to display a link to this route in the navigation
  displayAsNavLabel?:
    | boolean
    | ((coverPageContext: CoverPageContextType) => boolean)
  // Groups routes that should highlight the same nav item
  navGroup?: string
  // Maximum width of the immersive content for this route
  maxWidth?: string | number
}

export const immersiveRoutes: ImmersiveRouteConfig[] = [
  {
    key: "overview",
    navIcon: "list-one-column-fill",
    displayAsNavLabel: ({ resource }) => !!resource?.description,
    pathRe: "overview/",
    render: () => <CoverPageOverviewImmersive />,
    shouldRender: ({ resource }) => !!resource?.description,
    isActive: ({ matchPath }) => matchPath.includes("/overview/"),
    getPath: ({ baseCover }) => `${baseCover}/overview/`,
    getImmersiveRoute: () => ({ name: "overview" }),
    getNavigableImmersiveRoutes: ({ resource }) =>
      !!resource?.description ? [{ name: "overview" }] : [],
    maxWidth: "650px"
  },
  {
    key: "trailer",
    navIcon: "video",
    pathRe: "trailer/",
    displayAsNavLabel: ({ resource }) => !!resource?.trailer_embed_code,
    render: () => <CoverPageTrailerImmersive />,
    shouldRender: ({ resource }) => !!resource?.trailer_embed_code,
    isActive: ({ matchPath }) => matchPath.includes("/trailer/"),
    getPath: ({ baseCover }) => `${baseCover}/trailer/`,
    getImmersiveRoute: () => ({ name: "trailer" }),
    getNavigableImmersiveRoutes: ({ resource }) =>
      !!resource?.trailer_embed_code ? [{ name: "trailer" }] : [],
    maxWidth: "900px"
  },
  // {
  //   key: "contributors",
  //   navLabel: "Contributors",
  //   navIcon: "avatar-circle-fill",
  //   pathRe: "contributors/",
  //   shouldRender: ({ contributors }) => contributors?.length > 0,
  //   render: ({ id }: { id: number }) => (
  //     <CoverPageContributorImmersive id={id} />
  //   ),
  //   isActive: ({ matchPath }) => matchPath.includes("/contributors/"),
  //   getPath: ({ baseCover }) => `${baseCover}/contributors/`,
  //   getImmersiveRoute: () => ({ name: "contributors" }),
  //   getNavigableImmersiveRoutes: () => [],
  //   displayAsNavLabel: ({ contributors }) => contributors?.length > 0
  // },
  {
    key: "contributor",
    navGroup: "contributors",
    navIcon: "avatar-circle-fill",
    displayAsNavLabel: ({ contributors }) => contributors?.length > 0,
    pathRe: "contributor/:id(\\d+)/",
    shouldRender: ({ contributors }) => contributors?.length > 0,
    render: ({ id }: { id: number }) => (
      <CoverPageContributorImmersive id={id} />
    ),
    isActive: ({ matchPath, params }) =>
      matchPath.includes("/contributor/") && !!params?.id,
    getPath: ({ baseCover, route }) =>
      route.name === "contributor"
        ? `${baseCover}/contributor/${Number(route.id)}/`
        : null,
    getImmersiveRoute: (params: any) => {
      if (params?.id == null) return null
      return {
        name: "contributor",
        id: Number(params.id)
      }
    },
    getNavigableImmersiveRoutes: ({ contributors }) =>
      contributors?.length > 0
        ? contributors.map((c) => ({
            name: "contributor",
            id: c.id as number
          }))
        : [],
    maxWidth: "650px"
  },
  // {
  //   key: "previews",
  //   navLabel: "Preview",
  //   navIcon: "path",
  //   pathRe: "previews/",
  //   shouldRender: ({ previewNavItems }) => previewNavItems?.length > 0,
  //   render: ({ id }: { id: number | "path" }) => (
  //     <CoverPagePreviewImmersive id={id} />
  //   ),
  //   isActive: ({ matchPath }) => matchPath.includes("/previews/"),
  //   getPath: ({ baseCover }) => `${baseCover}/previews/`,
  //   getImmersiveRoute: () => ({ name: "previews" }),
  //   getNavigableImmersiveRoutes: () => [],
  //   displayAsNavLabel: true
  // },
  {
    key: "preview",
    navIcon: "eye",
    navGroup: "previews",
    displayAsNavLabel: ({ previewNavItems }) => previewNavItems?.length > 0,
    pathRe: "preview/:id(path|\\d+)/",
    shouldRender: ({ previewNavItems }) => previewNavItems?.length > 0,
    render: ({ id }: { id: number | "path" }) => (
      <CoverPagePreviewImmersive id={id} />
    ),
    isActive: ({ matchPath, params }) =>
      matchPath.includes("/preview/") && !!params?.id,
    getPath: ({ baseCover, route }) => {
      if (route.name !== "preview") return null
      const id = route.id
      if (id === "path") return `${baseCover}/preview/path/`
      if (typeof id === "number" && Number.isFinite(id))
        return `${baseCover}/preview/${id}/`
      return null
    },
    getImmersiveRoute: (params: any) => {
      if (params?.id == null) return null
      const raw = params.id
      const id = raw === "path" ? "path" : Number(raw)
      return { name: "preview", id }
    },
    getNavigableImmersiveRoutes: ({ previewNavItems }) =>
      previewNavItems.map((item) => ({
        name: "preview",
        id: item.source_id
      })),
    maxWidth: "800px"
  },
  {
    key: "offers",
    pathRe: "offers/",
    render: () => <CoverPageOfferSandboxImmersive />,
    displayAsNavLabel: false,
    isActive: ({ matchPath }) => matchPath.includes("/offers/"),
    getPath: ({ baseCover }) => `${baseCover}/offers/`,
    getImmersiveRoute: () => ({ name: "offers" }),
    // Offers sandbox route is intentionally non-navigable in the standard immersive nav.
    getNavigableImmersiveRoutes: () => [],
    maxWidth: "1200px"
  },
  {
    key: "offer",
    navIcon: "chevron-right",
    navGroup: "offers",
    displayAsNavLabel: ({ offers }) => offers?.length > 0,
    pathRe: "offer/:id(\\d+)/",
    shouldRender: ({ offers }) => offers?.length > 0,
    render: ({ id }: { id: number }) => <CoverPageOfferImmersive id={id} />,
    isActive: ({ matchPath, params }) =>
      matchPath.includes("/offer/") && !!params?.id,
    getPath: ({ baseCover, route }) =>
      route.name === "offer" ? `${baseCover}/offer/${Number(route.id)}/` : null,
    getImmersiveRoute: (params: any) => {
      if (params?.id == null) return null
      return { name: "offer", id: Number(params.id) }
    },
    getNavigableImmersiveRoutes: ({ offers }) =>
      offers?.length > 0
        ? offers.map((o) => ({
            name: "offer",
            id: o.id as number
          }))
        : [],
    maxWidth: "650px"
  },
  {
    key: "more",
    navIcon: "ellipses-o",
    pathRe: "more/",
    displayAsNavLabel: ({ moreResources, categories }) =>
      moreResources?.length > 0 && categories?.length > 0,
    shouldRender: ({ moreResources, categories }) =>
      moreResources?.length > 0 && categories?.length > 0,
    render: () => <CoverPageMore />,
    isActive: ({ matchPath }) => matchPath.includes("/more/"),
    getPath: ({ baseCover }) => `${baseCover}/more/`,
    getImmersiveRoute: () => ({ name: "more" }),
    getNavigableImmersiveRoutes: ({ moreResources, categories }) =>
      moreResources?.length > 0 && categories?.length > 0
        ? [{ name: "more" }]
        : [],
    maxWidth: "900px"
  }
]
